import React from 'react';
import {
  Layout,
  Avatar,
  Dropdown,
  Typography,
  Space,
  Button,
} from 'antd';
import {
  BookOutlined,
  UserOutlined,
  LogoutOutlined,
  SettingOutlined,
  HomeOutlined,
  FileTextOutlined,
  FolderOutlined,
  FormOutlined,
  MenuOutlined,
} from '@ant-design/icons';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { useResponsive } from '../../hooks/useResponsive';
import authService from '../../services/authService';
import { THEME_COLORS, COMMON_STYLES } from '../../utils/theme';

const { Header, Content } = Layout;
const { Text } = Typography;

interface MainLayoutProps {
  children: React.ReactNode;
}

const MainLayout: React.FC<MainLayoutProps> = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { isMobile } = useResponsive();
  
  const currentUser = authService.getCurrentUser();

  const handleLogout = () => {
    // Clear user data immediately
    authService.logout();
    // Use replace to prevent back navigation and immediate redirect
    window.location.replace('/login');
  };

  const userMenuItems = [
    {
      key: 'profile',
      icon: <UserOutlined />,
      label: 'Profile',
      onClick: () => navigate('/profile'),
    },
    // {
    //   key: 'settings',
    //   icon: <SettingOutlined />,
    //   label: 'Settings',
    //   onClick: () => navigate('/settings'),
    // },
    { type: 'divider' as const },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: 'Logout',
      onClick: handleLogout,
    },
  ];

  const mobileNavMenuItems = [
    {
      key: 'dashboard',
      icon: <HomeOutlined />,
      label: 'Home',
      onClick: () => navigate('/dashboard'),
    },
    {
      key: 'exams',
      icon: <FileTextOutlined />,
      label: 'Exam',
      onClick: () => navigate('/exams'),
    },
    {
      key: 'shared-files',
      icon: <FolderOutlined />,
      label: 'Files',
      onClick: () => navigate('/shared-files'),
    },
    {
      key: 'assignments',
      icon: <FormOutlined />,
      label: 'Assignments',
      onClick: () => navigate('/assignments'),
    },
  ];

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Header style={{
        padding: isMobile ? '0 16px' : '0 24px',
        background: '#fff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        boxShadow: COMMON_STYLES.cardShadow,
        borderBottom: `1px solid ${THEME_COLORS.gray2}`,
        position: 'sticky',
        top: 0,
        zIndex: 99,
        height: isMobile ? '64px' : '64px',
      }}>
        {/* Logo and Navigation */}
        <Space size={isMobile ? "small" : "large"} align="center">
          <Link to="/dashboard" style={{ textDecoration: 'none' }}>
            <Space align="center">
              <img 
                src="/images/logo.png" 
                alt="ICER LMS" 
                style={{ 
                  height: isMobile ? '36px' : '36px',
                  width: 'auto'
                }} 
              />
            </Space>
          </Link>
          
          {!isMobile && (
            <>
              <Link to="/dashboard" style={{ textDecoration: 'none' }}>
                <Button 
                  type={location.pathname === '/dashboard' ? 'primary' : 'text'} 
                  icon={<HomeOutlined />} 
                  size="middle"
                  style={{
                    border: location.pathname === '/dashboard' ? `1px solid ${THEME_COLORS.primaryBlue}` : 'none',
                    borderRadius: '8px',
                    backgroundColor: location.pathname === '/dashboard' ? THEME_COLORS.primaryBlue : 'transparent'
                  }}
                >
                  Home
                </Button>
              </Link>
              
              {/* <Link to="/courses" style={{ textDecoration: 'none' }}>
                <Button 
                  type={location.pathname === '/courses' ? 'primary' : 'text'} 
                  icon={<BookOutlined />} 
                  size="middle"
                  style={{
                    border: location.pathname === '/courses' ? `1px solid ${THEME_COLORS.primaryBlue}` : 'none',
                    borderRadius: '8px',
                    backgroundColor: location.pathname === '/courses' ? THEME_COLORS.primaryBlue : 'transparent'
                  }}
                >
                  Сургалт
                </Button>
              </Link> */}
              
              <Link to="/exams" style={{ textDecoration: 'none' }}>
                <Button 
                  type={location.pathname === '/exams' ? 'primary' : 'text'} 
                  icon={<FileTextOutlined />} 
                  size="middle"
                  style={{
                    border: location.pathname === '/exams' ? `1px solid ${THEME_COLORS.primaryBlue}` : 'none',
                    borderRadius: '8px',
                    backgroundColor: location.pathname === '/exams' ? THEME_COLORS.primaryBlue : 'transparent'
                  }}
                >
                  Exam
                </Button>
              </Link>
              
              <Link to="/shared-files" style={{ textDecoration: 'none' }}>
                <Button 
                  type={location.pathname === '/shared-files' ? 'primary' : 'text'} 
                  icon={<FolderOutlined />} 
                  size="middle"
                  style={{
                    border: location.pathname === '/shared-files' ? `1px solid ${THEME_COLORS.primaryBlue}` : 'none',
                    borderRadius: '8px',
                    backgroundColor: location.pathname === '/shared-files' ? THEME_COLORS.primaryBlue : 'transparent'
                  }}
                >
                  Files
                </Button>
              </Link>
              
              <Link to="/assignments" style={{ textDecoration: 'none' }}>
                <Button 
                  type={location.pathname === '/assignments' ? 'primary' : 'text'} 
                  icon={<FormOutlined />} 
                  size="middle"
                  style={{
                    border: location.pathname === '/assignments' ? `1px solid ${THEME_COLORS.primaryBlue}` : 'none',
                    borderRadius: '8px',
                    backgroundColor: location.pathname === '/assignments' ? THEME_COLORS.primaryBlue : 'transparent'
                  }}
                >
                  Assigments
                </Button>
              </Link>
            </>
          )}
        </Space>
        
        {/* User Menu */}
        <Space size={isMobile ? 8 : 0}>
          {/* Mobile Navigation Dropdown */}
          {isMobile && (
            <Dropdown 
              menu={{ 
                items: mobileNavMenuItems,
                selectedKeys: [location.pathname.replace('/', '') || 'dashboard']
              }} 
              placement="bottomRight"
              trigger={['click']}
            >
              <Button
                type="text"
                icon={<MenuOutlined />}
                size="large"
                style={{
                  height: '40px',
                  width: '40px',
                  borderRadius: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              />
            </Dropdown>
          )}
          
          {/* Profile Dropdown */}
          <Dropdown menu={{ items: userMenuItems }} placement="bottomRight">
            <Space style={{ cursor: 'pointer' }}>
              <Avatar 
                src={currentUser?.avatar} 
                icon={<UserOutlined />}
                size={isMobile ? "default" : "large"}
              />
              {!isMobile && (
                <Text strong style={{ fontSize: '14px' }}>
                  {currentUser?.first_name || currentUser?.username || 'User'}
                </Text>
              )}
            </Space>
          </Dropdown>
        </Space>
      </Header>
      
      <Content
        style={{
          margin: isMobile ? '16px' : '24px',
          overflow: 'initial',
        }}
      >
        {children}
      </Content>
    </Layout>
  );
};

export default MainLayout;
